import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Input } from "@porchpass/ui";
import DashboardLayout from "src/layouts/DashboardLayout";
import { addCountryCode, isValidPhoneNumber, validEmail } from "src/utils";
import { useResponseRedirect } from "src/hooks";
import { useWebClientApi } from "src/api";
import { useDlaFormSubmit } from "src/hooks/useDlaFormSubmit";
import { useFormLoad } from "src/hooks/useFormLoad";
import {
  type CoborrowerInviteFormData,
  toCoborrowerInviteReq,
} from "src/api/adapters/application";
import DuplicateCoborrowerModal from "src/components/DuplicateCoborrowerModal";

export default () => {
  const navigate = useNavigate();
  const [inputValue, setInputValue] = useState("");
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const api = useWebClientApi();
  const { formData, updateField } = useFormLoad<CoborrowerInviteFormData>(
    {},
    () => Promise.resolve({}),
    () => ({}),
    { enabled: false },
  );
  const [submitHandler, loading, response, , , duplicateCoborrowerPrompt] =
    useDlaFormSubmit(
      (data: CoborrowerInviteFormData) =>
        api.inviteCoborrower(
          toCoborrowerInviteReq(data) as Parameters<
            typeof api.inviteCoborrower
          >[0],
        ),
      {
        duplicateCoborrowerPrompt: (data) => {
          if (typeof data.email === "string" && data.email) {
            return data.email;
          }
          if (typeof data.phone_number === "string" && data.phone_number) {
            return data.phone_number;
          }
          return inputValue;
        },
      },
    );
  useResponseRedirect("../../progress", response);

  const validateInput = () => {
    setIsError(true);
    setErrorMessage("Invalid email or phone number");
    if (!inputValue) {
      setErrorMessage("Must not be empty");
    } else if (isValidPhoneNumber(inputValue)) {
      setIsError(false);
      setErrorMessage("");
      updateField("phone_number")(addCountryCode(inputValue));
    } else if (validEmail(inputValue)) {
      setIsError(false);
      setErrorMessage("");
      updateField("email")(inputValue);
    }
  };

  useEffect(() => {
    if (formData.email || formData.phone_number) {
      void submitHandler(formData);
    }
  }, [formData.email, formData.phone_number]);

  return (
    <DashboardLayout
      pageTitle="Invite Coborrower"
      backHandler={() => navigate("../progress")}
    >
      <div
        className="box-border flex w-full flex-col items-center gap-[18px] p-4"
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
            validateInput();
          }
        }}
      >
        <h1
          className="mt-12 text-center text-2xl font-semibold"
          data-replay-safe
        >
          Invite a co-borrower via email or SMS
        </h1>
        <p
          className="mb-5 text-center text-sm text-[rgba(104,112,131,1)]"
          data-replay-safe
        >
          Before sending the invitation, please verify that the
          co-borrower&#39;s email or phone number is correct.
        </p>
        <Input
          label="Email or phone number"
          name="email_or_phone"
          value={inputValue}
          onChange={(event) => setInputValue(event.target.value)}
          error={isError}
          helperText={isError ? errorMessage : undefined}
          className="w-full"
        />
        <Button
          type="button"
          variant="primary"
          size="l"
          className="w-full max-w-[246px]"
          onClick={validateInput}
          loading={loading}
        >
          Send Invite
        </Button>
      </div>
      {duplicateCoborrowerPrompt && (
        <DuplicateCoborrowerModal
          isOpen={duplicateCoborrowerPrompt.isOpen}
          borrowerName={duplicateCoborrowerPrompt.borrowerName}
          closeHandler={duplicateCoborrowerPrompt.cancel}
          confirmHandler={duplicateCoborrowerPrompt.confirm}
          isLoading={loading}
        />
      )}
    </DashboardLayout>
  );
};
